// PromptGenerator/PromptGenerator.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faMagic, faImage, faVideo, faCog, faRocket } from '@fortawesome/free-solid-svg-icons';
import styles from './PromptGenerator.module.css';
import { useNotification } from '../../context/NotificationContext';

// Import local types and utils
import { PromptGeneratorProps, AnswersState } from './types';
import { analyzeImage, analyzeVideo } from './utils/mediaAnalysis';
import { getApiUrl } from '../../config/api';

// Import hooks and components
import MediaUploadModal from './components/MediaUploadModal';
import CharacterStatusNotification from './components/CharacterStatusNotification';
import CharacterInfoButton from './components/CharacterInfoButton';
import ProgressIndicator from './components/ProgressIndicator';
import ModeToggle from './components/ModeToggle';
import QuestionRenderer from './components/QuestionRenderer';
import ResultsViewer from './components/ResultsViewer';
import { usePromptFlow } from './hooks/usePromptFlow';
import { usePromptGeneration } from './hooks/usePromptGeneration';
import { useCharacterStatus } from './hooks/useCharacterStatus';

// Helper for initial state setup from editing draft logic
const getInitialStateFromLocation = (location: any) => {
  const isEditing = location.state?.editingPrompt && location.state?.promptId;
  if (isEditing) {
    const { userIdea, attributes, promptId, title } = location.state;
    
    // Simplified attribute parsing for initial answer
    const outputType = attributes.split('-')[0];
    const initialAnswers: AnswersState = { outputType };
    
    // Logic to enhance userIdea for editing (copied from old PromptGenerator.tsx)
    let enhancedUserIdea = userIdea || '';
    const styleInstruction = attributes.includes('animated') ? 'Render in animated 3D Pixar art style.' : 
                             attributes.includes('photorealistic') ? 'Render in photorealistic style.' :
                             'Render in high-quality style.';
    
    if (!enhancedUserIdea.includes(styleInstruction)) {
      enhancedUserIdea = `${enhancedUserIdea}\n\n${styleInstruction}`;
    }
    
    return {
      editingPromptId: promptId,
      editingPromptTitle: title || 'Untitled Prompt',
      initialAnswers: initialAnswers,
      initialQuestionId: 'outputType',
      initialUserIdea: enhancedUserIdea,
    };
  }

  // Default state for new prompt
  return {
    editingPromptId: null,
    editingPromptTitle: null,
    initialAnswers: {},
    initialQuestionId: 'outputType',
    initialUserIdea: '',
  };
};

const PromptGenerator: React.FC<PromptGeneratorProps> = ({ onPromptCreated, projectId }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation('promptGenerator');
  const { showNotification } = useNotification();
  
  // State for results and media analysis (local to this component)
  const [showResults, setShowResults] = useState(false);
  const [generatedPrompts, setGeneratedPrompts] = useState<string[]>([]);
  const [sceneDescription, setSceneDescription] = useState<string>('');
  const [mediaAnalyzing, setMediaAnalyzing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMediaType, setModalMediaType] = useState<'image' | 'video'>('image');
  
  // Initialization State
  const { editingPromptId, editingPromptTitle, initialAnswers, initialQuestionId, initialUserIdea } = 
    useMemo(() => getInitialStateFromLocation(location), [location]);
  
  // Prompt Flow Hook
  const {
    currentQuestionId, answers, isAdvancedMode, userIdea, setUserIdea, relevantQuestions,
    currentQuestionIndex, currentQuestion, isLastQuestion, isFirstQuestion, canProceed, 
    attributesString, handleAnswer, handleMultipleSelect, handleNext, handlePrevious, handleModeToggle,
  } = usePromptFlow({ initialAnswers, initialQuestionId, initialUserIdea });

  // Prompt Generation Hook
  const {
    isGenerating, handleGenerate, handleGenerateImageFromPrompt, shouldShowGenerateImageButton
  } = usePromptGeneration(
    projectId || '', userIdea, attributesString, answers, isAdvancedMode, editingPromptId, 
    i18n.language, // Add current UI language
    setGeneratedPrompts, setShowResults, onPromptCreated
  );
  
  // Character Status Hook
  const { checkCharacterStatus, characterStatusAnalysis, hasAnyMissingSteps } = useCharacterStatus();
  const [showNotificationBar, setShowNotificationBar] = useState(true);
  const hasCharacterIssues = characterStatusAnalysis?.length > 0 && hasAnyMissingSteps;

  // Logic to load prompts and check character status on project load
  useEffect(() => {
    const checkProjectCharacterStatus = async () => {
      if (!projectId) return;
      try {
        const response = await fetch(getApiUrl(`/prompt-generator/prompts/${projectId}`), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        });
        const result = await response.json();
        if (result.success && result.data) {
          checkCharacterStatus(result.data, projectId);
        }
      } catch (error) {
        console.error('❌ Error checking character status:', error);
      }
    };
    if (projectId && !location.state?.editingPrompt) {
      checkProjectCharacterStatus();
    }
  }, [projectId]);

  // Reset logic that integrates across hooks and state
  const handleReset = () => {
    setShowResults(false);
    setGeneratedPrompts([]);
    setSceneDescription('');
    // For a clean slate, reload the window to reset all complex state in hooks
    window.location.reload(); 
  };


  // --- Render Blocks ---

  // Renders the idea input field
  const renderIdeaInput = () => {
    return (
      <div className={styles.ideaInputSection}>
        <h2 className={styles.sectionTitle}>{t('promptGenerator.ideaInput.title')}</h2>
        <p className={styles.sectionSubtitle}>
          {t('promptGenerator.ideaInput.subtitle')}
        </p>
        
        <div className={styles.inputContainer}>
          <textarea
            className={styles.ideaTextarea}
            placeholder={t('promptGenerator.ideaInput.placeholder')}
            value={userIdea}
            onChange={(e) => setUserIdea(e.target.value)}
            rows={6}
          />
          
          <div className={styles.inputActions}>
            <button
              className={styles.uploadButton}
              onClick={() => { setModalMediaType('image'); setIsModalOpen(true); }}
            >
              <FontAwesomeIcon icon={faImage} />
              {t('promptGenerator.ideaInput.addReferenceMedia')}
            </button>
          </div>
        </div>

        {sceneDescription && (
          <div className={styles.sceneDescriptionCard}>
            <h4>{t('promptGenerator.ideaInput.sceneAnalysisTitle')}</h4>
            <p>{sceneDescription}</p>
          </div>
        )}
      </div>
    );
  };
  
  // Determine if we are in the final idea input phase
  const isIdeaInputPhase = isLastQuestion && canProceed;
  const currentStepTotal = relevantQuestions.length + 1; // +1 for the idea input phase


  // --- Main Render Logic ---
  
  if (showResults) {
    return (
      <ResultsViewer
        generatedPrompts={generatedPrompts}
        isGenerating={isGenerating}
        handleReset={handleReset}
        handleGenerateImageFromPrompt={handleGenerateImageFromPrompt}
        shouldShowGenerateImageButton={shouldShowGenerateImageButton}
        userIdea={userIdea}
      />
    );
  }

  return (
    <div className={styles.container}>
      {/* Character Info Bar */}
      {hasCharacterIssues && (
        <CharacterInfoButton onClick={() => setShowNotificationBar(!showNotificationBar)} hasCharacterIssues={hasCharacterIssues} />
      )}
      {showNotificationBar && hasCharacterIssues && (
        <CharacterStatusNotification isVisible={true} onDismiss={() => setShowNotificationBar(false)} characterStatusAnalysis={characterStatusAnalysis} />
      )}
      
      {/* Header & Mode Toggle */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faMagic} />
            {editingPromptId 
              ? `${t('promptGenerator.header.editingTitle')} ${editingPromptTitle}` 
              : t('promptGenerator.header.title')}
          </h1>
          <p className={styles.subtitle}>
            {editingPromptId 
              ? t('promptGenerator.header.editingSubtitle') 
              : t('promptGenerator.header.subtitle')}
          </p>
        </div>
        
        {/* CONDITIONAL RENDERING: Only show ModeToggle during the initial question phase */}
        {!isIdeaInputPhase && (
          <ModeToggle isAdvancedMode={isAdvancedMode} handleModeToggle={handleModeToggle} />
        )}
      </div>

      {/* Progress Indicator - CONDITIONAL RENDERING for the horizontal line/flow indicator */}
      {!isIdeaInputPhase && (
        <ProgressIndicator
          currentIndex={currentQuestionIndex} 
          totalQuestions={currentStepTotal}
        />
      )}
      
      {/* Main Content Area: Question Flow or Idea Input */}
      <div className={styles.questionSection} style={{ marginTop: '5rem' }}>
        {isIdeaInputPhase ? (
          renderIdeaInput()
        ) : (
          <QuestionRenderer
            currentQuestion={currentQuestion}
            answers={answers}
            isAdvancedMode={isAdvancedMode}
            handleAnswer={handleAnswer}
            handleMultipleSelect={handleMultipleSelect}
            setIsModalOpen={setIsModalOpen}
            setModalMediaType={setModalMediaType}
            sceneDescription={sceneDescription}
          />
        )}
      </div>

      {/* Navigation */}
      <div className={styles.navigationContainer}>
        <button
          className={styles.secondaryButton}
          onClick={handlePrevious}
          disabled={isFirstQuestion && !isIdeaInputPhase}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('promptGenerator.buttons.previous')}
        </button>

        {isIdeaInputPhase ? (
          <button
            className={styles.generateButton}
            onClick={() => handleGenerate(sceneDescription)}
            disabled={!userIdea.trim() || isGenerating}
          >
            <FontAwesomeIcon icon={faMagic} />
            {isGenerating ? t('promptGenerator.buttons.generating') : t('promptGenerator.buttons.generatePrompts')}
          </button>
        ) : (
          <button
            className={styles.primaryButton}
            onClick={handleNext}
            disabled={!canProceed}
          >
            {t('promptGenerator.buttons.next')}
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        )}
      </div>

      {/* Media Upload Modal */}
      <MediaUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUseDescription={(description) => {
          setSceneDescription(description);
          setUserIdea(prev => {
            const enhanced = prev ? `${prev}\n\nReference: ${description}` : description;
            return enhanced;
          });
          setIsModalOpen(false);
          if (!isIdeaInputPhase && currentQuestion?.type === 'upload-prompt') {
            handleNext();
          }
        }}
        mediaType={modalMediaType}
        analyzeMedia={async (file, mediaType, contextType) => {
          try {
            setMediaAnalyzing(true);
            const currentUserIdea = userIdea.trim() || 'Analyze this media';
            const currentAttributes = attributesString;
            const result = mediaType === 'image' 
              ? await analyzeImage(file, currentUserIdea, currentAttributes, contextType || 'scene enhancement') 
              : await analyzeVideo(file, currentUserIdea, currentAttributes, contextType || 'scene continuation');
            return result;
          } catch (error) {
            throw error;
          } finally {
            setMediaAnalyzing(false);
          }
        }}
        userIdea={userIdea.trim() || 'Analyze this media'}
        attributes={attributesString}
      />
    </div>
  );
};

export default PromptGenerator;